/**
 * CalendarTab — month grid with a coloured dot per event on its day.
 * Filled dot = published, ring = scheduled, dashed ring = still-a-draft.
 * Click a day to see its events; each event opens the draft's summary.
 */
import { useMemo, useState } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ImageIcon, Layers } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/charts/EmptyState';
import { cn } from '@/lib/utils';
import { useContentCalendar, type CalendarEvent } from '@/hooks/useContentCalendar';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export function CalendarTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [openIso, setOpenIso] = useState<string | null>(null);
  const q = useContentCalendar({ year, month });

  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const eventsByDay = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const e of q.data ?? []) {
      const key = e.when.toDateString();
      m.set(key, [...(m.get(key) ?? []), e]);
    }
    return m;
  }, [q.data]);

  const openEvents = openIso ? eventsByDay.get(new Date(openIso).toDateString()) ?? [] : [];

  function step(delta: number) {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setOpenIso(null);
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            {new Date(year, month, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => step(-1)}><ChevronLeft className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => { const t = new Date(); setYear(t.getFullYear()); setMonth(t.getMonth()); setOpenIso(null); }}>Today</Button>
            <Button size="sm" variant="outline" onClick={() => step(1)}><ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-2xs text-muted-foreground">
          {WEEKDAYS.map((w) => <div key={w} className="px-1 py-0.5 text-center font-medium">{w}</div>)}
        </div>

        {q.isLoading ? (
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 42 }).map((_, i) => <Skeleton key={i} className="aspect-square w-full" />)}
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {grid.map((cell) => {
              const inMonth = cell.getMonth() === month;
              const iso = cell.toISOString();
              const key = cell.toDateString();
              const evs = eventsByDay.get(key) ?? [];
              const isToday = cell.toDateString() === new Date().toDateString();
              const isOpen = openIso && new Date(openIso).toDateString() === key;
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => setOpenIso(evs.length > 0 ? iso : null)}
                  className={cn(
                    'flex aspect-square flex-col items-start gap-0.5 rounded-md border px-1.5 py-1 text-left text-2xs transition-colors',
                    inMonth ? 'border-border/40 bg-card' : 'border-transparent bg-transparent text-muted-foreground/40',
                    isToday && 'ring-1 ring-violet-500/40',
                    isOpen && 'ring-2 ring-violet-500',
                    evs.length > 0 && 'cursor-pointer hover:border-violet-500/40',
                  )}
                >
                  <span className={cn('tabular-nums', isToday && 'font-semibold text-violet-500')}>{cell.getDate()}</span>
                  {evs.length > 0 && (
                    <div className="mt-auto flex flex-wrap gap-0.5">
                      {evs.slice(0, 6).map((e) => (
                        <EventDot key={e.draft_id + e.when_type} e={e} />
                      ))}
                      {evs.length > 6 && <span className="text-2xs text-muted-foreground">+{evs.length - 6}</span>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-1 text-2xs text-muted-foreground">
          <LegendDot fill /> published
          <LegendDot ring /> scheduled
          <LegendDot ring dashed /> draft
        </div>
      </div>

      <DayPanel iso={openIso} events={openEvents} />
    </div>
  );
}

function EventDot({ e }: { e: CalendarEvent }) {
  const filled = e.when_type === 'published';
  const dashed = e.when_type === 'draft';
  return (
    <div
      className="h-2 w-2 rounded-full"
      style={filled
        ? { background: e.bucket_color }
        : { border: `1.5px ${dashed ? 'dashed' : 'solid'} ${e.bucket_color}` }}
      title={`${e.bucket_slug} · ${e.when_type}`}
    />
  );
}

function LegendDot({ fill, dashed }: { fill?: boolean; ring?: boolean; dashed?: boolean }) {
  return (
    <div className="h-2 w-2 rounded-full" style={fill
      ? { background: '#888' }
      : { border: `1.5px ${dashed ? 'dashed' : 'solid'} #888` }} />
  );
}

function DayPanel({ iso, events }: { iso: string | null; events: CalendarEvent[] }) {
  if (!iso) {
    return (
      <div className="rounded-md border border-dashed border-border/50 bg-muted/20 p-4 text-center text-xs text-muted-foreground">
        Click a day with dots to see the drafts / scheduled / published posts on it.
      </div>
    );
  }
  const d = new Date(iso);
  return (
    <Card>
      <CardContent className="space-y-3 p-3">
        <div className="text-sm font-medium">
          {d.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long' })}
        </div>
        {events.length === 0 ? (
          <EmptyState title="Nothing on this day" description="Compose a draft and pick a schedule time." />
        ) : (
          <div className="space-y-2">
            {events.map((e) => (
              <div key={e.draft_id + e.when_type} className="flex items-center gap-2 rounded-md border border-border/40 p-2">
                <div className="h-8 w-8 shrink-0 overflow-hidden rounded bg-muted/30">
                  {e.hero_r2_key ? (
                    <img src={`/api/media/${e.hero_r2_key}`} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground/60">
                      <ImageIcon className="h-3 w-3" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-2xs">
                    <span className="rounded px-1.5 py-0.5 uppercase text-white" style={{ background: e.bucket_color }}>
                      {e.bucket_slug}
                    </span>
                    <span className="capitalize text-muted-foreground">{e.post_kind}</span>
                    {e.post_kind === 'carousel' && <Layers className="h-3 w-3 text-muted-foreground" />}
                    <span className="ml-auto tabular-nums text-muted-foreground">
                      {e.when.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {e.caption && (
                    <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{e.caption}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const firstWeekday = (first.getDay() + 6) % 7; // Mon=0
  const start = new Date(year, month, 1 - firstWeekday);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return cells;
}
